import { useEffect, useMemo, useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { MagnifyingGlass, UserCircleCheck, Envelope, WhatsappLogo, ChatCircleDots, PaperPlaneTilt } from "@phosphor-icons/react";

/**
 * Live borrower search:
 * - Types in a query (phone, email, name prefix, LeLe ID)
 * - Backend searches; results shown as chips
 * - Selecting a result populates the parent form with linked user
 * - If nothing matches, shows "Invite via WhatsApp / SMS / Email" fallback
 */
export default function BorrowerSearch({ query, setQuery, selected, onSelect, onClear, borrowerName, borrowerPhone, borrowerEmail }) {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [focused, setFocused] = useState(false);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if ((query || "").trim().length < 2) {
      setResults([]);
      return;
    }
    debRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/users/search", { params: { q: query.trim() } });
        setResults(data.results || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [query]);

  const inviteMsg = useMemo(() => {
    const name = borrowerName || "there";
    return `Hi ${name}, I'd like to document a loan between us on LeLe. Please install / sign in and accept the agreement I've drafted.`;
  }, [borrowerName]);

  const phoneDigits = (borrowerPhone || "").replace(/\D/g, "");
  const waLink = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(inviteMsg)}`;
  const smsLink = borrowerPhone
    ? `sms:${borrowerPhone}?body=${encodeURIComponent(inviteMsg)}`
    : "";
  const emailLink = borrowerEmail
    ? `mailto:${borrowerEmail}?subject=${encodeURIComponent("Your LeLe loan agreement")}&body=${encodeURIComponent(inviteMsg)}`
    : "";

  return (
    <div className="mb-2">
      <label className="block text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
        Find borrower on LeLe (phone / email / LeLe ID)
      </label>
      <div className="relative">
        <div className="relative">
          <MagnifyingGlass
            size={16}
            weight="bold"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
          />
          <input
            data-testid="borrower-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder="+91 98765 43210, ravi@x.com, or LL-XXXXXX"
            className="lele-input w-full rounded-lg pl-9 pr-4 py-3 text-sm"
          />
        </div>

        {focused && (results.length > 0 || (query || "").trim().length >= 2) && !selected && (
          <div className="absolute z-30 mt-2 w-full lele-card p-1 max-h-80 overflow-y-auto shadow-2xl">
            {searching ? (
              <div className="p-4 text-xs text-neutral-500">Searching…</div>
            ) : results.length > 0 ? (
              results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`borrower-search-result-${r.id}`}
                  onClick={() => onSelect(r)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition-colors text-left"
                >
                  <div className="h-8 w-8 rounded-full bg-indigo-500/15 grid place-items-center text-indigo-300 text-xs font-medium">
                    {(r.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-neutral-100 truncate">{r.name}</div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {r.phone || r.email || "—"} · <span className="text-indigo-300">{r.lele_id}</span>
                    </div>
                  </div>
                  <UserCircleCheck size={16} weight="fill" className="text-emerald-400" />
                </button>
              ))
            ) : (
              <div className="p-3">
                <div className="text-xs text-neutral-400 mb-2">
                  No LeLe user matched — <b className="text-neutral-200">invite them</b> to join:
                </div>
                <div className="flex flex-col gap-2">
                  <a
                    data-testid="invite-whatsapp"
                    href={waLink}
                    target="_blank"
                    rel="noreferrer"
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${phoneDigits ? "bg-emerald-500/[0.08] text-emerald-200 hover:bg-emerald-500/[0.14]" : "text-neutral-600 pointer-events-none"}`}
                  >
                    <WhatsappLogo size={14} weight="fill" /> WhatsApp invite {phoneDigits ? "" : "(add phone first)"}
                  </a>
                  <a
                    data-testid="invite-sms"
                    href={smsLink}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${borrowerPhone ? "bg-sky-500/[0.08] text-sky-200 hover:bg-sky-500/[0.14]" : "text-neutral-600 pointer-events-none"}`}
                  >
                    <ChatCircleDots size={14} weight="fill" /> SMS invite {borrowerPhone ? "" : "(add phone first)"}
                  </a>
                  <a
                    data-testid="invite-email"
                    href={emailLink}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${borrowerEmail ? "bg-fuchsia-500/[0.08] text-fuchsia-200 hover:bg-fuchsia-500/[0.14]" : "text-neutral-600 pointer-events-none"}`}
                  >
                    <Envelope size={14} weight="fill" /> Email invite {borrowerEmail ? "" : "(add email below)"}
                  </a>
                </div>
                <div className="text-[10px] text-neutral-500 mt-2 leading-relaxed">
                  You can still create the loan below — LeLe will keep it in
                  "Pending Acceptance" until the borrower joins.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selected && (
        <div
          data-testid="borrower-selected-chip"
          className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/[0.08] px-3 py-1.5 text-xs text-emerald-200"
        >
          <UserCircleCheck size={14} weight="fill" />
          Linked to {selected.name}
          {selected.lele_id ? ` · ${selected.lele_id}` : ""}
          <button
            data-testid="borrower-clear-selection"
            onClick={onClear}
            type="button"
            className="ml-1 text-emerald-200/70 hover:text-emerald-100"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
